"use client"

import React, { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import FormTextInput from '@/shared/components/form-inputs/form-text-input'
import FormTextareaInput from '@/shared/components/form-inputs/form-textarea-input'
import { Switch } from '@/shared/components/ui/switch'
import { Label } from '@/shared/components/ui/label'
import { useRouter } from 'next/navigation'
import { Info } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/components/ui/accordion'
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { buildRegionAwareHref } from '@/shared/lib/utils/region-url.utils'
import { Partner } from '../types/partner.type'
import { PartnerAddressAutocomplete } from './partner-address-autocomplete'

interface PartnerFormProps {
  partnerId?: string
  existingOrganizations?: Partner[]
  existingOrganizationsLoading?: boolean
}

export default function PartnerForm({ partnerId, existingOrganizations = [], existingOrganizationsLoading = false }: PartnerFormProps) {
  const router = useRouter()

  const { control, watch, setValue, formState: { isSubmitting } } = useFormContext()

  const isActive = watch('isActive')
  const regionalIsActive = watch('regionalIsActive')
  const regionalContactPerson = watch('regionalContactPerson')
  const regionalContactPhone = watch('regionalContactPhone')
  const regionalContactEmail = watch('regionalContactEmail')
  const mode = watch('mode') as 'new' | 'existing'
  const existingOrganizationId = watch('existingOrganizationId') as string | undefined

  const { regionSlug, region } = useRegionId()
  const isEditing = Boolean(partnerId)

  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const updateIsMobile = () => {
      if (typeof window === 'undefined') return
      setIsMobile(window.innerWidth < 768)
    }

    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)

    return () => window.removeEventListener('resize', updateIsMobile)
  }, [])

  useEffect(() => {
    if (mode === 'existing' && existingOrganizationId) {
      const selected = existingOrganizations.find((org) => String(org.id) === existingOrganizationId)
      if (selected) {
        setValue('name', selected.name ?? '', { shouldDirty: false })
        setValue('contactPerson', selected.contactPerson ?? '', { shouldDirty: false })
        setValue('phoneNumber', selected.phoneNumber ?? '', { shouldDirty: false })
        setValue('email', selected.email ?? '', { shouldDirty: false })
        setValue('specialties', selected.specialties ?? '', { shouldDirty: false })
        setValue('address', selected.address ?? '', { shouldDirty: false })
        setValue('city', selected.city ?? '', { shouldDirty: false })
        setValue('state', selected.state ?? '', { shouldDirty: false })
        setValue('zipCode', selected.zipCode ?? '', { shouldDirty: false })
        setValue('websiteUrl', selected.websiteUrl ?? '', { shouldDirty: false })
        setValue('giveLink', selected.giveLink ?? '', { shouldDirty: false })
        setValue('generalNotes', selected.generalNotes ?? '', { shouldDirty: false })
      }
    }

    if (mode === 'new' && existingOrganizationId) {
      setValue('existingOrganizationId', '', { shouldDirty: false })
    }
  }, [mode, existingOrganizationId, existingOrganizations, setValue, isEditing])

  const shouldCollapseMainContact = useMemo(() => {
    return isMobile
  }, [isMobile])

  const handleCancel = () => {
    const href = buildRegionAwareHref(regionSlug, '/partners')
    router.push(href)
  }

  const mainContactFields = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <FormTextInput
          name="contactPerson"
          control={control}
          placeholder="Primary contact person"
          inputGroupProps={{
            label: 'Contact Person',
          }}
          disabled={mode === 'existing'}
        />
      </div>

      <div className="space-y-2">
        <FormTextInput
          name="phoneNumber"
          control={control}
          placeholder="Phone number"
          inputGroupProps={{
            label: 'Phone Number',
          }}
          disabled={mode === 'existing'}
        />
      </div>

      <div className="space-y-2">
        <FormTextInput
          name="email"
          control={control}
          type="email"
          placeholder="Email address"
          inputGroupProps={{
            label: 'Email',
          }}
          disabled={mode === 'existing'}
        />
      </div>
    </div>
  )

  const selectedOrganization = useMemo(() => {
    if (!existingOrganizationId) return undefined
    return existingOrganizations.find((org) => String(org.id) === existingOrganizationId)
  }, [existingOrganizationId, existingOrganizations])

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{region?.name ? `${region.name} Regional Settings` : 'Regional Settings'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col gap-3">
            <div className="flex items-center space-x-2">
              <Switch
                id="regionalIsActive"
                checked={regionalIsActive}
                onCheckedChange={(checked) => setValue('regionalIsActive', checked)}
              />
              <Label htmlFor="regionalIsActive">Partner is active in this region</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-5 w-5 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                      aria-label="Regional active status information"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">Active organizations are included in the work scope Claimed By dropdowns.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <FormTextInput
                name="regionalContactPerson"
                control={control}
                placeholder="Regional contact person"
                inputGroupProps={{
                  label: 'Regional Contact Name',
                }}
              />
            </div>
            <div className="space-y-2">
              <FormTextInput
                name="regionalContactPhone"
                control={control}
                placeholder="Regional phone number"
                inputGroupProps={{
                  label: 'Regional Contact Phone',
                }}
              />
            </div>
            <div className="space-y-2">
              <FormTextInput
                name="regionalContactEmail"
                control={control}
                type="email"
                placeholder="Regional email address"
                inputGroupProps={{
                  label: 'Regional Contact Email',
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Organization Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {!isEditing && (
            <div className="space-y-3">
              <Label className="text-sm font-medium text-muted-foreground">How would you like to add this organization?</Label>
              <RadioGroup
                className="grid grid-cols-1 gap-2 md:grid-cols-2"
                value={mode}
                onValueChange={(value) => setValue('mode', value as 'new' | 'existing')}
              >
                <Label
                  htmlFor="mode-new"
                  className={`flex cursor-pointer flex-col gap-1 rounded-md border p-3 transition ${mode === 'new' ? 'border-primary bg-primary/5' : 'border-border'}`}
                >
                  <RadioGroupItem value="new" id="mode-new" className="sr-only" />
                  <span className="font-medium">Create new organization</span>
                  <span className="text-sm text-muted-foreground">Add a brand-new partner and associate it with this region.</span>
                </Label>
                <Label
                  htmlFor="mode-existing"
                  className={`flex cursor-pointer flex-col gap-1 rounded-md border p-3 transition ${mode === 'existing' ? 'border-primary bg-primary/5' : 'border-border'}`}
                >
                  <RadioGroupItem value="existing" id="mode-existing" className="sr-only" />
                  <span className="font-medium">Add existing organization to region</span>
                  <span className="text-sm text-muted-foreground">Select a partner already in the system and associate it with this region.</span>
                </Label>
              </RadioGroup>
            </div>
          )}

          {mode === 'existing' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium leading-none">Select organization</Label>
                <Select
                  value={existingOrganizationId ?? ''}
                  onValueChange={(value) => setValue('existingOrganizationId', value)}
                  disabled={existingOrganizationsLoading || existingOrganizations.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={existingOrganizationsLoading ? 'Loading organizations…' : 'Choose an organization'} />
                  </SelectTrigger>
                  <SelectContent>
                    {existingOrganizations.map((org) => (
                      <SelectItem key={org.id} value={String(org.id)}>
                        {org.name}
                      </SelectItem>
                    ))}
                    {existingOrganizations.length === 0 && !existingOrganizationsLoading && (
                      <SelectItem value="__no-options" disabled>
                        No organizations available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {selectedOrganization && (
                <div className="rounded-md border bg-muted/50 p-4 text-sm text-muted-foreground">
                  <p className="font-medium text-foreground">{selectedOrganization.name}</p>
                  <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                    {selectedOrganization.contactPerson && <p><span className="font-medium">Contact:</span> {selectedOrganization.contactPerson}</p>}
                    {selectedOrganization.phoneNumber && <p><span className="font-medium">Phone:</span> {selectedOrganization.phoneNumber}</p>}
                    {selectedOrganization.email && <p><span className="font-medium">Email:</span> {selectedOrganization.email}</p>}
                    {selectedOrganization.specialties && <p><span className="font-medium">Specialties:</span> {selectedOrganization.specialties}</p>}
                    {(selectedOrganization.address || selectedOrganization.city || selectedOrganization.state || selectedOrganization.zipCode) && (
                      <p className="md:col-span-2">
                        <span className="font-medium">Address:</span>{' '}
                        {[selectedOrganization.address, selectedOrganization.city, selectedOrganization.state, selectedOrganization.zipCode].filter(Boolean).join(', ')}
                      </p>
                    )}
                    {selectedOrganization.websiteUrl && (
                      <p>
                        <span className="font-medium">Website:</span>{' '}
                        <a href={selectedOrganization.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          {selectedOrganization.websiteUrl}
                        </a>
                      </p>
                    )}
                    {selectedOrganization.giveLink && (
                      <p>
                        <span className="font-medium">Giving Link:</span>{' '}
                        <a href={selectedOrganization.giveLink} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          {selectedOrganization.giveLink}
                        </a>
                      </p>
                    )}
                  </div>
                  {selectedOrganization.generalNotes && (
                    <p className="mt-2"><span className="font-medium">Notes:</span> {selectedOrganization.generalNotes}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {(mode === 'new' || isEditing) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <FormTextInput
                  name="name"
                  control={control}
                  placeholder="Enter organization name"
                  inputGroupProps={{
                    label: 'Organization Name *',
                  }}
                />
              </div>
              <div className="space-y-2">
                <FormTextInput
                  name="specialties"
                  control={control}
                  placeholder="e.g., Construction, Electrical, Plumbing, HVAC"
                  inputGroupProps={{
                    label: 'Specialties',
                  }}
                />
              </div>
            </div>
          )}

          {(mode === 'new' || isEditing) && (
            shouldCollapseMainContact ? (
              <Accordion type="single" collapsible defaultValue={undefined}>
                <AccordionItem value="main-contact">
                  <AccordionTrigger className="px-2">Partner Contact Details</AccordionTrigger>
                  <AccordionContent>{mainContactFields}</AccordionContent>
                </AccordionItem>
              </Accordion>
            ) : (
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-muted-foreground">Partner Contact Details</h4>
                {mainContactFields}
              </div>
            )
          )}

          {(mode === 'new' || isEditing) && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-3 space-y-2">
                  <PartnerAddressAutocomplete
                    control={control}
                    disabled={mode === 'existing'}
                  />
                </div>
                <div className="space-y-2">
                  <FormTextInput
                    name="city"
                    control={control}
                    placeholder="City"
                    inputGroupProps={{
                      label: 'City',
                    }}
                    disabled={mode === 'existing'}
                  />
                </div>
                <div className="space-y-2">
                  <FormTextInput
                    name="state"
                    control={control}
                    placeholder="State"
                    inputGroupProps={{
                      label: 'State',
                    }}
                    disabled={mode === 'existing'}
                  />
                </div>
                <div className="space-y-2">
                  <FormTextInput
                    name="zipCode"
                    control={control}
                    placeholder="ZIP Code"
                    inputGroupProps={{
                      label: 'ZIP Code',
                    }}
                    disabled={mode === 'existing'}
                  />
                </div>
              </div>
            </div>
          )}

          {(mode === 'new' || isEditing) && (
            <div className="space-y-2">
              <FormTextInput
                name="websiteUrl"
                control={control}
                type="url"
                placeholder="https://example.com"
                inputGroupProps={{
                  label: 'Website URL',
                }}
              />
            </div>
          )}

          {(mode === 'new' || isEditing) && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Giving Link
                </label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center w-4 h-4 text-muted-foreground hover:text-foreground transition-colors shrink-0"
                        aria-label="Giving link information"
                      >
                        <Info className="w-3.5 h-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Any partner with a giving link here will show up on the base page in the give tab
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <FormTextInput
                name="giveLink"
                control={control}
                type="url"
                placeholder="https://example.com/give"
              />
            </div>
          )}

          {(mode === 'new' || isEditing) && (
            <div className="space-y-2">
              <FormTextareaInput
                name="generalNotes"
                control={control}
                placeholder="Additional notes about the organization"
                rows={4}
                inputGroupProps={{
                  label: 'General Notes',
                }}
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : partnerId ? 'Update Partner' : 'Save Partner Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
